'use client';

import { useEffect } from 'react';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Checkbox from '@mui/material/Checkbox';
import ListItemText from '@mui/material/ListItemText';
import OutlinedInput from '@mui/material/OutlinedInput';
import IconButton from '@mui/material/IconButton';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

interface Option {
	label: string;
	value: string;
}

interface FilterSelectProps {
	label: string;
	options: Option[];
	value: string[] | null;
	multiple?: boolean;
	onChange: (newValue: string[] | null) => void;
}

export default function FilterSelect({
	label,
	options,
	value,
	multiple = false,
	onChange,
}: Readonly<FilterSelectProps>) {
	const selectValue = Array.isArray(value) ? value : [];

	useEffect(() => {
		const validValues = selectValue.filter((val) => options.some((opt) => opt.value === val));
		if (validValues.length !== selectValue.length) {
			onChange(validValues.length ? validValues : null);
		}
	}, [options, selectValue, onChange]);

	const handleChange = (event: SelectChangeEvent<typeof selectValue>) => {
		const val = event.target.value;
		const arrVal = typeof val === 'string' ? val.split(',') : val;
		onChange(arrVal.length > 0 ? arrVal : null);
	};

	const handleClear = () => {
		onChange(null);
	};

	const getSelectedLabels = (selected: string[]): string[] => {
		return selected
			.map((val) => options.find((opt) => opt.value === val)?.label)
			.filter((label): label is string => Boolean(label));
	};

	return (
		<FormControl fullWidth data-testid="filter-select">
			<InputLabel
				id={`${label}-label`}
				sx={{
					'&.Mui-focused': {
						color: 'grey.dark',
					},
				}}
			>
				{label}
			</InputLabel>
			<Select
				labelId={`${label}-label`}
				id={`${label}-select`}
				multiple={multiple}
				value={selectValue}
				onChange={handleChange}
				input={<OutlinedInput label={label} />}
				renderValue={() => getSelectedLabels(selectValue).join(', ')}
				IconComponent={ExpandMoreIcon}
				sx={{
					'& .MuiSelect-iconOutlined': { display: selectValue.length > 0 ? 'none' : '', mr: 1, color: 'primary.main' },
					'& .MuiOutlinedInput-notchedOutline': {
						borderColor: 'grey.dark',
					},
					backgroundColor: 'white.main',
				}}
				endAdornment={
					selectValue.length > 0 && (
						<IconButton onClick={handleClear} data-testid="clear-button" sx={{ mr: 1 }}>
							<HighlightOffIcon />
						</IconButton>
					)
				}
			>
				{options.map((option) => (
					<MenuItem key={option.value} value={option.value}>
						{multiple && <Checkbox checked={selectValue.some((val) => val === option.value)} />}
						<ListItemText primary={option.label} />
					</MenuItem>
				))}
			</Select>
		</FormControl>
	);
}
